import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { motion } from "framer-motion";
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import {
    HiChevronDown,
    HiOutlineExclamation
} from "react-icons/hi";
import { Loader } from "rizzui";
import useFetch from "../../../../hooks/useFetch";
import { PickupGlobalMessageService } from "../../../../services/pickup/pickupGlobalMessageService";
import { PickupGlobalMessage, PickupGlobalMessagesApiResponse } from "../../../../type/entities/pickup-global-message-type";
import cn from "../../../../utils/classNames";
import { pickupMessagesTypes } from "../../../../utils/SelectorMapping";

interface GlobalMessagesProps {
    className?: string;
}

const getMessageBgColor = (color?: string) => {
    if (color) {
        // Si hay un color personalizado, usarlo como fondo con opacidad
        return `border-l-4 shadow-sm`;
    }
    // Colores por defecto si no hay color personalizado
    return 'bg-gray-50 border-gray-200 border-l-4 shadow-sm';
};

const isMessageActive = (message: PickupGlobalMessage): boolean => {
    return Boolean(message.isActive);
};

const GlobalMessages: FC<GlobalMessagesProps> = ({ className }) => {
    const service = new PickupGlobalMessageService();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [visibleCount, setVisibleCount] = useState(5);
    const [filteredMessages, setFilteredMessages] = useState<PickupGlobalMessage[]>([]);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listPickupGlobalMessages({
            limit: 100, // Obtener más mensajes para filtrar localmente
            filter_filters: {
                isActive: 1,
                actual_date: moment().format('YYYY-MM-DD HH:mm')
            }
        });
        return response.getResponseData() as PickupGlobalMessagesApiResponse;
    }, []));

    useEffect(() => {
        if (data?.data) {
            const now = new Date();
            const filtered = data.data.filter((message: PickupGlobalMessage) => {
                // Verificar si el mensaje está activo
                if (!isMessageActive(message)) return false;

                // Verificar fechas
                const startDate = message.startDate ? new Date(message.startDate as unknown as string) : null;
                const endDate = message.endDate ? new Date(message.endDate as unknown as string) : null;

                // Si hay fecha de inicio, verificar que ya haya comenzado
                if (startDate && now < startDate) return false;

                // Si hay fecha de fin, verificar que no haya terminado
                if (endDate && now > endDate) return false;

                return true;
            });
            setFilteredMessages(filtered);
        }
    }, [data]);

    const handleToggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleViewMore = () => {
        setVisibleCount(prev => prev + 5);
    };

    const handleViewLess = () => {
        setVisibleCount(prev => Math.max(5, prev - 5));
    };

    const displayedMessages = filteredMessages.slice(0, visibleCount);
    const canViewMore = visibleCount < filteredMessages.length;
    const canViewLess = visibleCount > 5;

    if (loading) return <Loader size='sm' />;
    if (error) return null;
    if (filteredMessages.length === 0) return null;

    return (
        <div className={cn("space-y-4", className)}>
            {/* Header con dropdown */}
            <div
                className="flex items-center justify-between p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={handleToggleCollapse}
            >
                <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-800">
                        Mensajes Globales ({filteredMessages.length})
                    </h3>
                </div>
                <div className="flex items-center space-x-2">
                    <HiChevronDown className={`w-5 h-5 text-gray-600 transition-transform duration-700 ease-in-out ${isCollapsed ? 'rotate-0' : 'rotate-180'}`} />
                </div>
            </div>

            {/* Contenido de mensajes */}
            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1200px] opacity-100'}`}>
                <div className="space-y-3">
                    {displayedMessages.map((message: PickupGlobalMessage, index: number) => (
                        <motion.div
                            key={message.id}
                            initial={{ opacity: 0, y: -10 }}    // estado inicial (invisible y un poquito arriba)
                            animate={{ opacity: 1, y: 0 }}      // estado al aparecer
                            exit={{ opacity: 0, y: 10 }}        // estado al desaparecer (fade out y baja)
                            transition={{ duration: 0.5, delay: index * 0.05 }}      // duración de la animación
                            className={cn(
                                "p-4 rounded-lg",
                                getMessageBgColor(message.color)
                            )}
                            style={{
                                backgroundColor: message.color ? `${message.color}20` : undefined,
                                borderLeftColor: message.color || '#d1d5db'
                            }}
                        >
                            <div className="flex items-start space-x-3">
                                <div className={cn("flex-shrink-0 mt-0.5")}>
                                    {(() => {
                                        const Icon = pickupMessagesTypes.find(
                                            (item) => item.value === message.type
                                        )?.icon || HiOutlineExclamation;
                                        return <Icon className="h-5 w-5 text-gray-800" />;
                                    })()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                        <h4 className={cn("text-sm font-bold capitalize")}>
                                            {message.concept || 'Mensaje Global'}
                                        </h4>
                                        <span className="text-xs text-gray-500">
                                            {message.createdAt && formatDistance(
                                                new Date(message.createdAt as unknown as string),
                                                new Date(),
                                                { addSuffix: true, locale: es }
                                            )}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-sm text-gray-600">
                                        {message.message}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Botones de paginación */}
                {(canViewMore || canViewLess) && (
                    <div className="flex justify-center space-x-2 mt-4">
                        {canViewLess && (
                            <button
                                onClick={handleViewLess}
                                className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Ver menos
                            </button>
                        )}
                        {canViewMore && (
                            <button
                                onClick={handleViewMore}
                                className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Ver más ({filteredMessages.length - visibleCount} restantes)
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GlobalMessages;